import { useEffect, useMemo, useState } from "react";
import API from "../services/api";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from "recharts";



const CHART_COLORS = [
    "#2563eb",
    "#16a34a",
    "#dc2626",
    "#f59e0b",
    "#7c3aed",
    "#0891b2",
    "#db2777",
    "#65a30d",
    "#ea580c",
    "#4f46e5"
];

export default function SalaryHistory() {

    const [history, setHistory] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [chartType, setChartType] = useState("bar");

    const [loading, setLoading] = useState(false);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [expenseType, setExpenseType] = useState("");
    const [paymentMode, setPaymentMode] = useState("");
    const [staffId, setStaffId] = useState("");
    const [academicYear, setAcademicYear] = useState("");

    const [search, setSearch] = useState("");

    const [staffList, setStaffList] = useState([]);
    const [academicYears, setAcademicYears] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);

    const recordsPerPage = 20;


    /* =========================================
       LOAD STAFF
    ========================================= */

    const fetchStaff = async () => {

        try {

            const res = await API.get("/staff");

            setStaffList(res.data || []);

        } catch (err) {

            console.log("Unable to load staff", err);

        }

    };


    /* =========================================
       LOAD ACADEMIC YEARS
    ========================================= */

    const fetchAcademicYears = async () => {

        try {

            const res =
                await API.get(
                    "/settings/academic-years"
                );

            console.log(
                "ACADEMIC YEARS:",
                res.data
            );

            const years =
                Array.isArray(res.data)
                    ? res.data
                    : [];

            setAcademicYears(years);

            const activeYear =
                years.find(
                    year =>
                        Number(year.is_active) === 1
                );

            if (activeYear) {

                setAcademicYear(
                    activeYear.academic_year
                );

            }

        } catch (err) {

            console.log(
                "Unable to load academic years",
                err
            );

        }

    };


    /* =========================================
       BUILD QUERY
    ========================================= */

    const buildQuery = () => {

        const params = new URLSearchParams();

        if (fromDate)
            params.append("fromDate", fromDate);

        if (toDate)
            params.append("toDate", toDate);

        if (expenseType)
            params.append(
                "expense_type",
                expenseType
            );

        if (paymentMode)
            params.append(
                "payment_mode",
                paymentMode
            );

        if (staffId)
            params.append(
                "staff_id",
                staffId
            );

        if (academicYear)
            params.append(
                "academic_year",
                academicYear
            );

        if (search)
            params.append(
                "search",
                search
            );

        return params.toString();

    };


    /* =========================================
       FETCH HISTORY
    ========================================= */

    const fetchHistory = async () => {

        try {

            setLoading(true);

            const query =
                buildQuery();

            const res =
                await API.get(
                    `/salary/history?${query}`
                );

            setHistory(
                Array.isArray(res.data)
                    ? res.data
                    : []
            );

            setCurrentPage(1);

        } catch (err) {

            console.log(err);

            alert(
                "Unable to fetch expense history"
            );

        } finally {

            setLoading(false);

        }

    };


    /* =========================================
       FETCH ANALYTICS
    ========================================= */
    const fetchAnalytics = async () => {

        try {

            const query = buildQuery();

            const res =
                await API.get(
                    `/salary/analytics?${query}`
                );

            console.log(
                "SALARY ANALYTICS:",
                res.data
            );

            setAnalytics(
                res.data
            );

        } catch (err) {

            console.log(err);

            alert(
                "Unable to load expense analytics"
            );

        }

    };


    /* =========================================
       LOAD EVERYTHING
    ========================================= */

    const loadData = async () => {

        await Promise.all([
            fetchHistory(),
            fetchAnalytics()
        ]);

    };


    /* =========================================
       INITIAL LOAD
    ========================================= */

    useEffect(() => {

        fetchStaff();
        fetchAcademicYears();
        loadData();

    }, []);


    /* =========================================
       QUICK FILTER - THIS MONTH
    ========================================= */

    const thisMonth = () => {

        const now = new Date();

        const firstDay =
            new Date(
                now.getFullYear(),
                now.getMonth(),
                1
            );

        const lastDay =
            new Date(
                now.getFullYear(),
                now.getMonth() + 1,
                0
            );

        const formatDate = (date) => {

            const year =
                date.getFullYear();

            const month =
                String(
                    date.getMonth() + 1
                ).padStart(2, "0");

            const day =
                String(
                    date.getDate()
                ).padStart(2, "0");

            return `${year}-${month}-${day}`;

        };

        setFromDate(
            formatDate(firstDay)
        );

        setToDate(
            formatDate(lastDay)
        );

    };


    /* =========================================
       QUICK FILTER - LAST MONTH
    ========================================= */

    const lastMonth = () => {

        const now = new Date();

        const firstDay =
            new Date(
                now.getFullYear(),
                now.getMonth() - 1,
                1
            );

        const lastDay =
            new Date(
                now.getFullYear(),
                now.getMonth(),
                0
            );

        const formatDate = (date) => {

            const year =
                date.getFullYear();

            const month =
                String(
                    date.getMonth() + 1
                ).padStart(2, "0");

            const day =
                String(
                    date.getDate()
                ).padStart(2, "0");

            return `${year}-${month}-${day}`;

        };

        setFromDate(
            formatDate(firstDay)
        );

        setToDate(
            formatDate(lastDay)
        );

    };


    /* =========================================
       CLEAR FILTERS
    ========================================= */

    const resetFilters = () => {

        setFromDate("");
        setToDate("");
        setExpenseType("");
        setPaymentMode("");
        setStaffId("");
        setAcademicYear("");
        setSearch("");

        setTimeout(() => {

            loadData();

        }, 0);

    };


    /* =========================================
       APPLY FILTERS
    ========================================= */

    const applyFilters = async () => {

        await loadData();

        setTimeout(() => {

            document
                .getElementById("transaction-history")
                ?.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                });

        }, 100);

    };

    /* =========================================
       PAGINATION
    ========================================= */

    const totalPages =
        Math.ceil(
            history.length /
            recordsPerPage
        );

    const startIndex =
        (currentPage - 1) *
        recordsPerPage;

    const paginatedHistory =
        history.slice(
            startIndex,
            startIndex + recordsPerPage
        );


    /* =========================================
       LOCAL FILTERED TOTAL
    ========================================= */

    const pageTotal =
        paginatedHistory.reduce(
            (sum, item) =>
                sum + Number(item.amount || 0),
            0
        );


    /* =========================================
       FORMAT MONEY
    ========================================= */

    const money = (value) => {

        return new Intl.NumberFormat(
            "en-IN",
            {
                style: "currency",
                currency: "INR",
                maximumFractionDigits: 2
            }
        ).format(
            Number(value || 0)
        );

    };


    /* =========================================
       EXPORT EXCEL
    ========================================= */

    const exportExcel = () => {

        const query =
            buildQuery();

        window.open(
            `${API.defaults.baseURL}/salary/export?${query}`,
            "_blank"
        );

    };


    /* =========================================
       EXPORT PDF
    ========================================= */

    const exportPDF = () => {

        const query =
            buildQuery();

        window.open(
            `${API.defaults.baseURL}/salary/export-pdf?${query}`,
            "_blank"
        );

    };


    return (

        <div className="p-6 bg-gray-50 min-h-screen">

            {/* =====================================
          HEADER
      ===================================== */}

            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">

                <div>

                    <h1 className="text-3xl font-bold">
                        Expenses & Salary History
                    </h1>

                    <p className="text-gray-500 mt-1">
                        Monitor, analyse and download school
                        financial expenses
                    </p>

                </div>


                <div className="flex gap-2 mt-4 md:mt-0">

                    <button
                        onClick={exportExcel}
                        className="bg-green-600 text-white px-4 py-2 rounded"
                    >
                        Excel
                    </button>

                    <button
                        onClick={exportPDF}
                        className="bg-red-600 text-white px-4 py-2 rounded"
                    >
                        PDF
                    </button>

                </div>

            </div>


            {/* =====================================
          QUICK FILTERS
      ===================================== */}

            <div className="bg-white shadow rounded p-5 mb-6">

                <div className="flex flex-wrap gap-2 mb-5">

                    <button
                        onClick={thisMonth}
                        className="border px-4 py-2 rounded hover:bg-gray-100"
                    >
                        This Month
                    </button>

                    <button
                        onClick={lastMonth}
                        className="border px-4 py-2 rounded hover:bg-gray-100"
                    >
                        Last Month
                    </button>

                    <button
                        onClick={async () => {

                            try {

                                const res = await API.get(
                                    "/settings/active-academic-year"
                                );

                                const activeYear =
                                    res.data?.academic_year;

                                if (!activeYear) {

                                    alert(
                                        "Active academic year not found"
                                    );

                                    return;

                                }

                                setAcademicYear(activeYear);

                                setFromDate("");
                                setToDate("");

                                const params =
                                    new URLSearchParams();

                                params.append(
                                    "academic_year",
                                    activeYear
                                );

                                const [historyRes, analyticsRes] =
                                    await Promise.all([

                                        API.get(
                                            `/salary/history?${params.toString()}`
                                        ),

                                        API.get(
                                            `/salary/analytics?${params.toString()}`
                                        )

                                    ]);

                                setHistory(
                                    Array.isArray(historyRes.data)
                                        ? historyRes.data
                                        : []
                                );

                                setAnalytics(
                                    analyticsRes.data
                                );

                                setCurrentPage(1);

                                setTimeout(() => {

                                    document
                                        .getElementById(
                                            "transaction-history"
                                        )
                                        ?.scrollIntoView({
                                            behavior: "smooth",
                                            block: "start"
                                        });

                                }, 100);

                            } catch (err) {

                                console.log(err);

                                alert(
                                    "Unable to load active academic year"
                                );

                            }

                        }}
                        className="border px-4 py-2 rounded hover:bg-gray-100"
                    >
                        This Academic Year
                    </button>

                </div>


                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                    <div>

                        <label className="block text-sm font-semibold mb-1">
                            From Date
                        </label>

                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) =>
                                setFromDate(
                                    e.target.value
                                )
                            }
                            className="border p-2 rounded w-full"
                        />

                    </div>


                    <div>

                        <label className="block text-sm font-semibold mb-1">
                            To Date
                        </label>

                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) =>
                                setToDate(
                                    e.target.value
                                )
                            }
                            className="border p-2 rounded w-full"
                        />

                    </div>


                    <div>

                        <label className="block text-sm font-semibold mb-1">
                            Expense Type
                        </label>

                        <select
                            value={expenseType}
                            onChange={(e) =>
                                setExpenseType(
                                    e.target.value
                                )
                            }
                            className="border p-2 rounded w-full"
                        >

                            <option value="">
                                All Expenses
                            </option>

                            <option value="Salary">
                                Salary
                            </option>

                            <option value="Electricity">
                                Electricity
                            </option>

                            <option value="Food">
                                Food
                            </option>

                            <option value="Gas">
                                Gas
                            </option>

                            <option value="Furniture">
                                Furniture
                            </option>

                            <option value="Transport">
                                Transport
                            </option>

                            <option value="Maintenance">
                                Maintenance
                            </option>

                            <option value="Books">
                                Books
                            </option>

                            <option value="Other">
                                Other
                            </option>

                        </select>

                    </div>


                    <div>

                        <label className="block text-sm font-semibold mb-1">
                            Payment Mode
                        </label>

                        <select
                            value={paymentMode}
                            onChange={(e) =>
                                setPaymentMode(
                                    e.target.value
                                )
                            }
                            className="border p-2 rounded w-full"
                        >

                            <option value="">
                                All Modes
                            </option>

                            <option value="Cash">
                                Cash
                            </option>

                            <option value="Bank Transfer">
                                Bank Transfer
                            </option>

                            <option value="UPI">
                                UPI
                            </option>

                            <option value="Cheque">
                                Cheque
                            </option>

                            <option value="Card">
                                Card
                            </option>

                            <option value="Other">
                                Other
                            </option>

                        </select>

                    </div>


                    <div>

                        <label className="block text-sm font-semibold mb-1">
                            Staff
                        </label>

                        <select
                            value={staffId}
                            onChange={(e) =>
                                setStaffId(
                                    e.target.value
                                )
                            }
                            className="border p-2 rounded w-full"
                        >

                            <option value="">
                                All Staff
                            </option>

                            {staffList.map(
                                (staff) => (

                                    <option
                                        key={staff.id}
                                        value={staff.id}
                                    >
                                        {staff.name}
                                    </option>

                                )
                            )}

                        </select>

                    </div>


                    <div>

                        <label className="block text-sm font-semibold mb-1">
                            Academic Year
                        </label>

                        <select
                            value={academicYear}
                            onChange={(e) =>
                                setAcademicYear(
                                    e.target.value
                                )
                            }
                            className="border p-2 rounded w-full"
                        >

                            <option value="">
                                All Academic Years
                            </option>

                            {academicYears.map(
                                (year) => (

                                    <option
                                        key={year.id}
                                        value={
                                            year.academic_year
                                        }
                                    >
                                        {year.academic_year}
                                    </option>

                                )
                            )}

                        </select>

                    </div>


                    <div className="lg:col-span-2">

                        <label className="block text-sm font-semibold mb-1">
                            Search
                        </label>

                        <input
                            type="text"
                            placeholder="Description, staff, reference..."
                            value={search}
                            onChange={(e) =>
                                setSearch(
                                    e.target.value
                                )
                            }
                            className="border p-2 rounded w-full"
                        />

                    </div>

                </div>


                <div className="flex gap-3 mt-5">

                    <button
                        onClick={applyFilters}
                        className="bg-blue-600 text-white px-6 py-2 rounded"
                    >
                        Apply Filters
                    </button>

                    <button
                        onClick={resetFilters}
                        className="border px-6 py-2 rounded"
                    >
                        Reset
                    </button>

                </div>

            </div>


            {/* =====================================
          ANALYTICS CARDS
      ===================================== */}

            {analytics && (

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">

                    {/*<div className="bg-white shadow rounded p-5">*/}

                    {/*    <p className="text-gray-500 text-sm">*/}
                    {/*        Filtered Total*/}
                    {/*    </p>*/}

                    {/*    <p className="text-2xl font-bold mt-2">*/}
                    {/*        {money(*/}
                    {/*            analytics.filteredTotal*/}
                    {/*        )}*/}
                    {/*    </p>*/}

                    {/*</div>*/}


                    <div className="bg-white shadow rounded p-5">

                        <p className="text-gray-500 text-sm">
                            Salary
                        </p>

                        <p className="text-2xl font-bold mt-2">
                            {money(
                                analytics.salaryTotal
                            )}
                        </p>

                    </div>


                    <div className="bg-white shadow rounded p-5">

                        <p className="text-gray-500 text-sm">
                            Other Expenses
                        </p>

                        <p className="text-2xl font-bold mt-2">
                            {money(
                                analytics.otherExpenseTotal
                            )}
                        </p>

                    </div>


                    <div className="bg-white shadow rounded p-5">

                        <p className="text-gray-500 text-sm">
                            This Month
                        </p>

                        <p className="text-2xl font-bold mt-2">
                            {money(
                                analytics.thisMonthTotal
                            )}
                        </p>

                    </div>


                    <div className="bg-white shadow rounded p-5">

                        <p className="text-gray-500 text-sm">
                            Academic Year
                        </p>

                        <p className="text-2xl font-bold mt-2">
                            {money(
                                analytics.academicYearTotal
                            )}
                        </p>

                    </div>


                    <div className="bg-white shadow rounded p-5">

                        <p className="text-gray-500 text-sm">
                            Transactions
                        </p>

                        <p className="text-2xl font-bold mt-2">
                            {analytics.transactionCount}
                        </p>

                    </div>

                </div>

            )}









            {/* =====================================
          TRANSACTION TABLE
      ===================================== */}



            <div className="bg-white shadow rounded p-6">

                <div className="flex justify-between items-center mb-5">

                    <div id="transaction-history" className="bg-white shadow rounded p-6"  >

                        <h2 className="text-xl font-bold">
                            Transaction History
                        </h2>

                        <p className="text-sm text-gray-500">
                            Showing {history.length} records
                        </p>

                    </div>

                    {loading && (
                        <span className="text-blue-600">
                            Loading...
                        </span>
                    )}

                </div>


                <div className="overflow-x-auto">

                    <table className="w-full border-collapse">

                        <thead>

                            <tr className="bg-gray-100">

                                <th className="border p-3 text-left">
                                    Date
                                </th>

                                <th className="border p-3 text-left">
                                    Type
                                </th>

                                <th className="border p-3 text-left">
                                    Description
                                </th>

                                <th className="border p-3 text-left">
                                    Staff
                                </th>

                                <th className="border p-3 text-left">
                                    Month
                                </th>

                                <th className="border p-3 text-left">
                                    Amount
                                </th>

                                <th className="border p-3 text-left">
                                    Mode
                                </th>

                                <th className="border p-3 text-left">
                                    Reference
                                </th>
                                <th className="border p-3 text-left">
                                    Academic Year
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {paginatedHistory.map(
                                (item) => (

                                    <tr
                                        key={item.id}
                                        className="hover:bg-gray-50"
                                    >

                                        <td className="border p-3">
                                            {item.payment_date
                                                ? new Date(
                                                    item.payment_date
                                                ).toLocaleDateString(
                                                    "en-IN"
                                                )
                                                : "-"}
                                        </td>

                                        <td className="border p-3 font-medium">
                                            {item.expense_type}
                                        </td>

                                        <td className="border p-3">
                                            {item.description ||
                                                "-"}
                                        </td>

                                        <td className="border p-3">
                                            {item.staff_name ||
                                                "-"}
                                        </td>

                                        <td className="border p-3">
                                            {item.month ||
                                                "-"}
                                        </td>

                                        <td className="border p-3 font-semibold">
                                            {money(
                                                item.amount
                                            )}
                                        </td>

                                        <td className="border p-3">
                                            {item.payment_mode ||
                                                "-"}
                                        </td>

                                        <td className="border p-3">
                                            {item.reference_no ||
                                                "-"}
                                        </td>

                                        <td className="border p-3">
                                            {item.academic_year ||
                                                "-"}
                                        </td>

                                    </tr>

                                )
                            )}


                            {paginatedHistory.length === 0 && (

                                <tr>

                                    <td
                                        colSpan="9"
                                        className="text-center p-8 text-gray-500"
                                    >
                                        No financial records found.
                                    </td>

                                </tr>

                            )}

                        </tbody>

                    </table>

                </div>


                {/* =================================
            TOTALS
        ================================= */}
                {/* =================================
    FINANCIAL TOTALS
================================= */}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">

                    {/* Current Page Total */}

                    <div className="border rounded p-4">

                        <p className="text-gray-500">
                            Current Page Total
                        </p>

                        <p className="text-xl font-bold">
                            {money(pageTotal)}
                        </p>

                    </div>


                    {/* Academic Year Total */}

                    <div className="border rounded p-4">

                        <p className="text-gray-500">
                            Academic Year Total
                        </p>

                        <p className="text-xl font-bold">
                            {money(
                                analytics?.academicYearTotal
                            )}
                        </p>

                    </div>


                    {/* Grand Total */}

                    <div className="border rounded p-4">

                        <p className="text-gray-500">
                            Grand Total (All Time)
                        </p>

                        <p className="text-xl font-bold">
                            {money(
                                analytics?.grandTotal
                            )}
                        </p>

                    </div>

                </div>


                {/* =================================
            PAGINATION
        ================================= */}

                {totalPages > 1 && (

                    <div className="flex justify-center items-center gap-3 mt-6">

                        <button
                            disabled={
                                currentPage === 1
                            }
                            onClick={() =>
                                setCurrentPage(
                                    (page) =>
                                        page - 1
                                )
                            }
                            className="border px-4 py-2 rounded disabled:opacity-40"
                        >
                            Previous
                        </button>


                        <span>
                            Page {currentPage} of{" "}
                            {totalPages}
                        </span>


                        <button
                            disabled={
                                currentPage ===
                                totalPages
                            }
                            onClick={() =>
                                setCurrentPage(
                                    (page) =>
                                        page + 1
                                )
                            }
                            className="border px-4 py-2 rounded disabled:opacity-40"
                        >
                            Next
                        </button>

                    </div>



                )}

            </div>


            {/* =====================================
    ADVANCED SALARY ANALYTICS
===================================== */}

            {analytics && (
                <div className="space-y-6 mb-6">

                    {/* GLOBAL CHART SELECTOR */}

                    <div className="bg-white shadow rounded p-5">

                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

                            <div>
                                <h2 className="text-xl font-bold">
                                    Analytics Charts
                                </h2>

                                <p className="text-sm text-gray-500 mt-1">
                                    Choose how all analytics charts are displayed.
                                </p>
                            </div>


                            <div className="flex gap-2">

                                <button
                                    type="button"
                                    onClick={() => setChartType("bar")}
                                    className={`px-5 py-2 rounded border font-medium ${chartType === "bar"
                                        ? "bg-blue-600 text-white"
                                        : "bg-white text-gray-700 hover:bg-gray-100"
                                        }`}
                                >
                                    Bar
                                </button>


                                <button
                                    type="button"
                                    onClick={() => setChartType("line")}
                                    className={`px-5 py-2 rounded border font-medium ${chartType === "line"
                                        ? "bg-blue-600 text-white"
                                        : "bg-white text-gray-700 hover:bg-gray-100"
                                        }`}
                                >
                                    Line
                                </button>


                                <button
                                    type="button"
                                    onClick={() => setChartType("pie")}
                                    className={`px-5 py-2 rounded border font-medium ${chartType === "pie"
                                        ? "bg-blue-600 text-white"
                                        : "bg-white text-gray-700 hover:bg-gray-100"
                                        }`}
                                >
                                    Pie
                                </button>

                            </div>

                        </div>

                    </div>

                    {/* =================================
            SALARY MANAGEMENT CARDS
        ================================= */}

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

                        {/* Total Salary */}

                        <div className="bg-white shadow rounded p-5 border-l-4 border-blue-600">

                            <p className="text-gray-500 text-sm">
                                Total Salary Paid
                            </p>

                            <p className="text-2xl font-bold mt-2">
                                {money(
                                    analytics.salaryStats?.totalSalary
                                )}
                            </p>

                            <p className="text-xs text-gray-500 mt-1">
                                Selected period
                            </p>

                        </div>


                        {/* Staff Count */}

                        <div className="bg-white shadow rounded p-5 border-l-4 border-purple-600">

                            <p className="text-gray-500 text-sm">
                                Staff Paid
                            </p>

                            <p className="text-2xl font-bold mt-2">
                                {analytics.salaryStats?.staffCount || 0}
                            </p>

                            <p className="text-xs text-gray-500 mt-1">
                                Staff receiving salary
                            </p>

                        </div>


                        {/* Average Payment */}

                        <div className="bg-white shadow rounded p-5 border-l-4 border-green-600">

                            <p className="text-gray-500 text-sm">
                                Average Salary Payment
                            </p>

                            <p className="text-2xl font-bold mt-2">
                                {money(
                                    analytics.salaryStats?.averagePayment
                                )}
                            </p>

                            <p className="text-xs text-gray-500 mt-1">
                                Per salary transaction
                            </p>

                        </div>


                        {/* Top 5 */}

                        <div className="bg-white shadow rounded p-5 border-l-4 border-orange-500">

                            <p className="text-gray-500 text-sm">
                                Top 5 Staff Cost
                            </p>

                            <p className="text-2xl font-bold mt-2">
                                {money(
                                    analytics.topFiveSalary
                                )}
                            </p>

                            <p className="text-xs text-gray-500 mt-1">
                                Combined salary cost
                            </p>

                        </div>

                    </div>


                    {/* =================================
            SALARY BY STAFF
        ================================= */}

                    {analytics.salaryByStaff?.length > 0 && (

                        <div className="bg-white shadow rounded p-6">

                            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-5">

                                <div>

                                    <h2 className="text-xl font-bold">
                                        Salary Cost by Staff
                                    </h2>

                                    <p className="text-sm text-gray-500 mt-1">
                                        Shows who receives the highest total salary payments
                                        in the selected period.
                                    </p>

                                </div>

                            </div>


                            <div className="overflow-x-auto">

                                <table className="w-full border-collapse">

                                    <thead>

                                        <tr className="bg-gray-100">

                                            <th className="border p-3 text-left">
                                                #
                                            </th>

                                            <th className="border p-3 text-left">
                                                Staff
                                            </th>

                                            <th className="border p-3 text-left">
                                                Role
                                            </th>

                                            <th className="border p-3 text-right">
                                                Configured Salary
                                            </th>

                                            <th className="border p-3 text-right">
                                                Total Paid
                                            </th>

                                            <th className="border p-3 text-center">
                                                Payments
                                            </th>

                                            <th className="border p-3 text-center">
                                                Months
                                            </th>

                                            <th className="border p-3 text-right">
                                                Cost %
                                            </th>

                                        </tr>

                                    </thead>


                                    <tbody>

                                        {analytics.salaryByStaff.map(
                                            (item, index) => {

                                                const totalSalary =
                                                    Number(
                                                        analytics.salaryStats?.totalSalary || 0
                                                    );

                                                const percentage =
                                                    totalSalary > 0
                                                        ? (
                                                            Number(item.total_paid) /
                                                            totalSalary
                                                        ) * 100
                                                        : 0;

                                                return (

                                                    <tr
                                                        key={item.staff_id}
                                                        className="hover:bg-gray-50"
                                                    >

                                                        <td className="border p-3">
                                                            {index + 1}
                                                        </td>

                                                        <td className="border p-3 font-semibold">
                                                            {item.staff_name || "-"}
                                                        </td>

                                                        <td className="border p-3">
                                                            {item.staff_role || "-"}
                                                        </td>

                                                        <td className="border p-3 text-right">
                                                            {money(
                                                                item.configured_salary
                                                            )}
                                                        </td>

                                                        <td className="border p-3 text-right font-bold">
                                                            {money(
                                                                item.total_paid
                                                            )}
                                                        </td>

                                                        <td className="border p-3 text-center">
                                                            {item.payment_count}
                                                        </td>

                                                        <td className="border p-3 text-center">
                                                            {item.months_paid}
                                                        </td>

                                                        <td className="border p-3 text-right">

                                                            <span className="font-semibold">
                                                                {percentage.toFixed(1)}%
                                                            </span>

                                                        </td>

                                                    </tr>

                                                );

                                            }
                                        )}

                                    </tbody>

                                </table>

                            </div>

                        </div>

                    )}


                    {/* =================================
    SALARY COST CHART
================================= */}

                    {analytics.salaryByStaff?.length > 0 && (

                        <div className="bg-white shadow rounded p-6">

                            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-5">

                                <div>

                                    <h2 className="text-xl font-bold">
                                        Salary Cost Distribution
                                    </h2>

                                    <p className="text-sm text-gray-500 mt-1">
                                        Visual comparison of salary costs by staff.
                                    </p>

                                </div>


                                {/* CHART SELECTOR */}

                                <div className="flex gap-2 mt-4 md:mt-0">

                                    <button
                                        type="button"
                                        onClick={() => setChartType("bar")}
                                        className={`px-4 py-2 rounded border ${chartType === "bar"
                                            ? "bg-blue-600 text-white"
                                            : "bg-white text-gray-700 hover:bg-gray-100"
                                            }`}
                                    >
                                        Bar
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setChartType("line")}
                                        className={`px-4 py-2 rounded border ${chartType === "line"
                                            ? "bg-blue-600 text-white"
                                            : "bg-white text-gray-700 hover:bg-gray-100"
                                            }`}
                                    >
                                        Line
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setChartType("pie")}
                                        className={`px-4 py-2 rounded border ${chartType === "pie"
                                            ? "bg-blue-600 text-white"
                                            : "bg-white text-gray-700 hover:bg-gray-100"
                                            }`}
                                    >
                                        Pie
                                    </button>

                                </div>

                            </div>


                            {/* CHART */}

                            <div className="w-full h-[400px]">

                                <ResponsiveContainer width="100%" height="100%">

                                    {chartType === "bar" ? (

                                        <BarChart
                                            data={analytics.salaryByStaff}
                                            margin={{
                                                top: 20,
                                                right: 30,
                                                left: 20,
                                                bottom: 60
                                            }}
                                        >

                                            <CartesianGrid strokeDasharray="3 3" />

                                            <XAxis
                                                dataKey="staff_name"
                                                angle={-35}
                                                textAnchor="end"
                                                interval={0}
                                                height={80}
                                            />

                                            <YAxis />

                                            <Tooltip />

                                            <Legend />

                                            <Bar
                                                dataKey="total_paid"
                                                name="Salary Paid"
                                                fill="#2563eb"
                                            />

                                        </BarChart>

                                    ) : chartType === "line" ? (

                                        <LineChart
                                            data={analytics.salaryByStaff}
                                            margin={{
                                                top: 20,
                                                right: 30,
                                                left: 20,
                                                bottom: 60
                                            }}
                                        >

                                            <CartesianGrid strokeDasharray="3 3" />

                                            <XAxis
                                                dataKey="staff_name"
                                                angle={-35}
                                                textAnchor="end"
                                                interval={0}
                                                height={80}
                                            />

                                            <YAxis />

                                            <Tooltip />

                                            <Legend />

                                            <Line
                                                type="monotone"
                                                dataKey="total_paid"
                                                name="Salary Paid"
                                                stroke="#2563eb"
                                                strokeWidth={3}
                                                activeDot={{ r: 6 }}
                                            />

                                        </LineChart>

                                    ) : (

                                        <PieChart>

                                            <Pie
                                                data={analytics.salaryByStaff.map(item => ({
                                                    ...item,
                                                    total_paid: Number(item.total_paid) || 0
                                                }))}
                                                dataKey="total_paid"
                                                nameKey="staff_name"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={140}
                                                label
                                            >

                                                {analytics.salaryByStaff.map(
                                                    (item, index) => (

                                                        <Cell
                                                            key={`cell-${index}`}
                                                            fill={
                                                                [
                                                                    "#2563eb",
                                                                    "#16a34a",
                                                                    "#dc2626",
                                                                    "#f59e0b",
                                                                    "#7c3aed",
                                                                    "#0891b2",
                                                                    "#db2777",
                                                                    "#65a30d"
                                                                ][index % 8]
                                                            }
                                                        />

                                                    )
                                                )}

                                            </Pie>

                                            <Tooltip />

                                            <Legend />

                                        </PieChart>

                                    )}

                                </ResponsiveContainer>

                            </div>

                        </div>

                    )}

                    {/* =================================
    SALARY BY ROLE
================================= */}

                    {analytics.salaryByRole?.length > 0 && (

                        <div className="bg-white shadow rounded p-6">

                            <h2 className="text-xl font-bold mb-1">
                                Salary Cost by Role
                            </h2>

                            <p className="text-sm text-gray-500 mb-5">
                                Understand which staff categories account for salary expenditure.
                            </p>


                            <div className="w-full h-[400px]">

                                <ResponsiveContainer width="100%" height="100%">

                                    {chartType === "bar" ? (

                                        <BarChart
                                            data={analytics.salaryByRole}
                                            margin={{
                                                top: 20,
                                                right: 30,
                                                left: 20,
                                                bottom: 60
                                            }}
                                        >

                                            <CartesianGrid strokeDasharray="3 3" />

                                            <XAxis
                                                dataKey="staff_role"
                                                angle={-35}
                                                textAnchor="end"
                                                interval={0}
                                                height={80}
                                            />

                                            <YAxis />

                                            <Tooltip />

                                            <Legend />

                                            <Bar
                                                dataKey="total_paid"
                                                name="Salary Paid"
                                                fill="#7c3aed"
                                            />

                                        </BarChart>

                                    ) : chartType === "line" ? (

                                        <LineChart
                                            data={analytics.salaryByRole}
                                            margin={{
                                                top: 20,
                                                right: 30,
                                                left: 20,
                                                bottom: 60
                                            }}
                                        >

                                            <CartesianGrid strokeDasharray="3 3" />

                                            <XAxis
                                                dataKey="staff_role"
                                                angle={-35}
                                                textAnchor="end"
                                                interval={0}
                                                height={80}
                                            />

                                            <YAxis />

                                            <Tooltip />

                                            <Legend />

                                            <Line
                                                type="monotone"
                                                dataKey="total_paid"
                                                name="Salary Paid"
                                                stroke="#7c3aed"
                                                strokeWidth={3}
                                                activeDot={{ r: 6 }}
                                            />

                                        </LineChart>

                                    ) : (

                                        <PieChart>

                                            <Pie
                                                data={analytics.salaryByRole.map(item => ({
                                                    name: item.staff_role || "Unassigned",
                                                    value: Number(item.total_paid) || 0
                                                }))}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={140}
                                                label
                                            >
                                                {analytics.salaryByRole.map((item, index) => (
                                                    <Cell
                                                        key={`role-cell-${index}`}
                                                        fill={
                                                            [
                                                                "#2563eb",
                                                                "#16a34a",
                                                                "#dc2626",
                                                                "#f59e0b",
                                                                "#7c3aed",
                                                                "#0891b2",
                                                                "#db2777",
                                                                "#65a30d"
                                                            ][index % 8]
                                                        }
                                                    />
                                                ))}
                                            </Pie>

                                            <Tooltip />

                                            <Legend />

                                        </PieChart>

                                    )}

                                </ResponsiveContainer>

                            </div>

                        </div>

                    )}

                    {/* =================================
    MONTHLY SALARY TREND
================================= */}

                    {analytics.salaryMonthlyTrend?.length > 0 && (

                        <div className="bg-white shadow rounded p-6">

                            <h2 className="text-xl font-bold mb-1">
                                Monthly Salary Cost
                            </h2>

                            <p className="text-sm text-gray-500 mb-5">
                                Track how salary expenditure changes month by month.
                            </p>


                            <div className="w-full h-[400px]">

                                <ResponsiveContainer width="100%" height="100%">

                                    {chartType === "bar" ? (

                                        <BarChart
                                            data={analytics.salaryMonthlyTrend.map(item => ({
                                                ...item,
                                                total: Number(item.total) || 0
                                            }))}
                                            margin={{
                                                top: 20,
                                                right: 30,
                                                left: 20,
                                                bottom: 20
                                            }}
                                        >

                                            <CartesianGrid strokeDasharray="3 3" />

                                            <XAxis dataKey="month" />

                                            <YAxis />

                                            <Tooltip />

                                            <Legend />

                                            <Bar
                                                dataKey="total"
                                                name="Salary Cost"
                                                fill="#16a34a"
                                            />

                                        </BarChart>

                                    ) : chartType === "line" ? (

                                        <LineChart
                                            data={analytics.salaryMonthlyTrend.map(item => ({
                                                ...item,
                                                total: Number(item.total) || 0
                                            }))}
                                            margin={{
                                                top: 20,
                                                right: 30,
                                                left: 20,
                                                bottom: 20
                                            }}
                                        >

                                            <CartesianGrid strokeDasharray="3 3" />

                                            <XAxis dataKey="month" />

                                            <YAxis />

                                            <Tooltip />

                                            <Legend />

                                            <Line
                                                type="monotone"
                                                dataKey="total"
                                                name="Salary Cost"
                                                stroke="#16a34a"
                                                strokeWidth={3}
                                                activeDot={{ r: 6 }}
                                            />

                                        </LineChart>

                                    ) : (

                                        <PieChart>

                                            <Pie
                                                data={analytics.salaryMonthlyTrend.map(item => ({
                                                    ...item,
                                                    total: Number(item.total) || 0
                                                }))}
                                                dataKey="total"
                                                nameKey="month"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={140}
                                                label
                                            >

                                                {analytics.salaryMonthlyTrend.map(
                                                    (item, index) => (

                                                        <Cell
                                                            key={`salary-month-cell-${index}`}
                                                            fill={
                                                                [
                                                                    "#2563eb",
                                                                    "#16a34a",
                                                                    "#dc2626",
                                                                    "#f59e0b",
                                                                    "#7c3aed",
                                                                    "#0891b2",
                                                                    "#db2777",
                                                                    "#65a30d"
                                                                ][index % 8]
                                                            }
                                                        />

                                                    )
                                                )}

                                            </Pie>

                                            <Tooltip />

                                            <Legend />

                                        </PieChart>

                                    )}

                                </ResponsiveContainer>

                            </div>

                        </div>

                    )}




                    {/* =================================
    SALARY VS OTHER EXPENSES
================================= */}

                    {analytics.salaryTotal !== undefined && (

                        <div className="bg-white shadow rounded p-6">

                            <h2 className="text-xl font-bold mb-1">
                                Salary vs Other Expenses
                            </h2>

                            <p className="text-sm text-gray-500 mb-5">
                                Compare payroll expenditure against all other school expenses.
                            </p>


                            {(() => {

                                const salary = Number(
                                    analytics.salaryTotal || 0
                                );

                                const other = Number(
                                    analytics.otherExpenseTotal || 0
                                );

                                const chartData = [
                                    {
                                        name: "Salary",
                                        value: salary
                                    },
                                    {
                                        name: "Other Expenses",
                                        value: other
                                    }
                                ];


                                return (

                                    <div className="w-full h-[400px]">

                                        <ResponsiveContainer
                                            width="100%"
                                            height="100%"
                                        >

                                            {chartType === "bar" ? (

                                                <BarChart
                                                    data={chartData}
                                                    margin={{
                                                        top: 20,
                                                        right: 30,
                                                        left: 20,
                                                        bottom: 20
                                                    }}
                                                >

                                                    <CartesianGrid
                                                        strokeDasharray="3 3"
                                                    />

                                                    <XAxis
                                                        dataKey="name"
                                                    />

                                                    <YAxis />

                                                    <Tooltip />

                                                    <Legend />

                                                    <Bar
                                                        dataKey="value"
                                                        name="Amount"
                                                        fill="#2563eb"
                                                    />

                                                </BarChart>

                                            ) : chartType === "line" ? (

                                                <LineChart
                                                    data={chartData}
                                                    margin={{
                                                        top: 20,
                                                        right: 30,
                                                        left: 20,
                                                        bottom: 20
                                                    }}
                                                >

                                                    <CartesianGrid
                                                        strokeDasharray="3 3"
                                                    />

                                                    <XAxis
                                                        dataKey="name"
                                                    />

                                                    <YAxis />

                                                    <Tooltip />

                                                    <Legend />

                                                    <Line
                                                        type="monotone"
                                                        dataKey="value"
                                                        name="Amount"
                                                        stroke="#2563eb"
                                                        strokeWidth={3}
                                                        activeDot={{ r: 6 }}
                                                    />

                                                </LineChart>

                                            ) : (

                                                <PieChart>

                                                    <Pie
                                                        data={chartData}
                                                        dataKey="value"
                                                        nameKey="name"
                                                        cx="50%"
                                                        cy="50%"
                                                        outerRadius={140}
                                                        label
                                                    >

                                                        <Cell fill="#2563eb" />

                                                        <Cell fill="#dc2626" />

                                                    </Pie>

                                                    <Tooltip />

                                                    <Legend />

                                                </PieChart>

                                            )}

                                        </ResponsiveContainer>

                                    </div>

                                );

                            })()}

                        </div>

                    )}



                    {/* =====================================
    CATEGORY ANALYSIS
===================================== */}

                    {analytics &&
                        analytics.categories?.length > 0 && (

                            <div className="bg-white shadow rounded p-6 mb-6">

                                <h2 className="text-xl font-bold mb-1">
                                    Expense Breakdown
                                </h2>

                                <p className="text-sm text-gray-500 mb-5">
                                    Breakdown of expenses by category.
                                </p>


                                <div className="w-full h-[400px]">

                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                    >

                                        {chartType === "bar" ? (

                                            <BarChart
                                                data={analytics.categories}
                                                margin={{
                                                    top: 20,
                                                    right: 30,
                                                    left: 20,
                                                    bottom: 60
                                                }}
                                            >

                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                />

                                                <XAxis
                                                    dataKey="expense_type"
                                                    angle={-35}
                                                    textAnchor="end"
                                                    interval={0}
                                                    height={80}
                                                />

                                                <YAxis />

                                                <Tooltip />

                                                <Legend />

                                                <Bar
                                                    dataKey="total"
                                                    name="Expense"
                                                    fill="#2563eb"
                                                />

                                            </BarChart>

                                        ) : chartType === "line" ? (

                                            <LineChart
                                                data={analytics.categories}
                                                margin={{
                                                    top: 20,
                                                    right: 30,
                                                    left: 20,
                                                    bottom: 60
                                                }}
                                            >

                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                />

                                                <XAxis
                                                    dataKey="expense_type"
                                                    angle={-35}
                                                    textAnchor="end"
                                                    interval={0}
                                                    height={80}
                                                />

                                                <YAxis />

                                                <Tooltip />

                                                <Legend />

                                                <Line
                                                    type="monotone"
                                                    dataKey="total"
                                                    name="Expense"
                                                    stroke="#2563eb"
                                                    strokeWidth={3}
                                                    activeDot={{ r: 6 }}
                                                />

                                            </LineChart>

                                        ) : (

                                            <PieChart>

                                                <Pie
                                                    data={analytics.categories.map(item => ({
                                                        ...item,
                                                        total: Number(item.total) || 0
                                                    }))}
                                                    dataKey="total"
                                                    nameKey="expense_type"
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius={140}
                                                    label
                                                    labelLine
                                                >

                                                    {analytics.categories.map(
                                                        (item, index) => (

                                                            <Cell
                                                                key={`expense-category-${index}`}
                                                                fill={CHART_COLORS[index % CHART_COLORS.length]}
                                                            />

                                                        )
                                                    )}

                                                </Pie>

                                                <Tooltip />

                                                <Legend />

                                            </PieChart>

                                        )}

                                    </ResponsiveContainer>

                                </div>

                            </div>

                        )}

                    {/* =====================================
          MONTHLY TREND
      ===================================== */}

                    {/* =====================================
    MONTHLY TREND
===================================== */}

                    {analytics &&
                        analytics.monthlyTrend?.length > 0 && (

                            <div className="bg-white shadow rounded p-6 mb-6">

                                <h2 className="text-xl font-bold mb-1">
                                    Monthly Expense Trend
                                </h2>

                                <p className="text-sm text-gray-500 mb-5">
                                    Track how total expenses change month by month.
                                </p>


                                <div className="w-full h-[400px]">

                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                    >

                                        {chartType === "bar" ? (

                                            <BarChart
                                                data={analytics.monthlyTrend}
                                                margin={{
                                                    top: 20,
                                                    right: 30,
                                                    left: 20,
                                                    bottom: 20
                                                }}
                                            >

                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                />

                                                <XAxis
                                                    dataKey="month"
                                                />

                                                <YAxis />

                                                <Tooltip />

                                                <Legend />

                                                <Bar
                                                    dataKey="total"
                                                    name="Expenses"
                                                    fill="#16a34a"
                                                />

                                            </BarChart>

                                        ) : chartType === "line" ? (

                                            <LineChart
                                                data={analytics.monthlyTrend}
                                                margin={{
                                                    top: 20,
                                                    right: 30,
                                                    left: 20,
                                                    bottom: 20
                                                }}
                                            >

                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                />

                                                <XAxis
                                                    dataKey="month"
                                                />

                                                <YAxis />

                                                <Tooltip />

                                                <Legend />

                                                <Line
                                                    type="monotone"
                                                    dataKey="total"
                                                    name="Expenses"
                                                    stroke="#16a34a"
                                                    strokeWidth={3}
                                                    activeDot={{ r: 6 }}
                                                />

                                            </LineChart>

                                        ) : (

                                            <PieChart>

                                                <Pie
                                                    data={analytics.monthlyTrend.map(item => ({
                                                        ...item,
                                                        total: Number(item.total) || 0
                                                    }))}
                                                    dataKey="total"
                                                    nameKey="month"
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius={140}
                                                    label
                                                    labelLine
                                                >

                                                    {analytics.monthlyTrend.map(
                                                        (item, index) => (

                                                            <Cell
                                                                key={`expense-month-${index}`}
                                                                fill={CHART_COLORS[index % CHART_COLORS.length]}
                                                            />

                                                        )
                                                    )}

                                                </Pie>

                                                <Tooltip />

                                                <Legend />

                                            </PieChart>

                                        )}

                                    </ResponsiveContainer>

                                </div>

                            </div>

                        )}

                </div>

            )}

        </div>
    );
}
